/**
 * The findings list: filters, sorting, paging, and the imported corpora beside it.
 *
 * All three list features are SERVER-side, deliberately. A corpus grows without limit, and
 * sorting one page in the browser reorders the window while claiming to have sorted the
 * list. Text filters debounce; any filter change resets to page one.
 *
 * `imported_from` carries three meanings here and all of them matter: absent is everything,
 * '' is the reader's own work alone, and an author id is one quarantine. Collapsing the
 * first two is how somebody else's findings end up in a pack.
 */

import type { JSX } from 'react'

/**
 * The study workspace: the cards a reader works their own corpus in.
 *
 * Separate from `cards.tsx`, which is the *text* — passages, evidence, the
 * lexicon. That file is what the product is for; this one is where the reader
 * keeps what they found. They share the output window and its stack, because a
 * finding is a passage-shaped thing and belongs where passages live, but they
 * are different jobs and a single thousand-line file would hide that.
 *
 * Two rules from the design shape everything here:
 *
 * * **Cards are data, never closures.** Every card in the stack is a plain
 *   value — `{ kind: 'finding', id }`, never `{ onSave: () => … }` — so the
 *   stack stays serialisable and navigation is not coupled to whoever pushed
 *   it. Anything a card needs from React state arrives as a prop on the window.
 * * **Filtering, sorting and paging happen on the server.** A corpus grows
 *   without limit; a page that ships every row and hides most of them gets
 *   slower every time the reader does good work. Sorting one page in the
 *   browser would be worse still — it would reorder the window while claiming
 *   to have sorted the list.
 */

import { useEffect, useState } from 'react'
import type { FindingPage, FindingSort, FindingSummary } from '../account'
import { deleteFinding, listFindings, retireFinding } from '../account'
import type { Card } from '../cards'
import { t } from '../i18n'
import { BringFindingsIn } from './bringIn'
import { ImportedSections, Subscriptions } from './imports'

import { FILTER_DEBOUNCE_MS, Failure, Loading, PAGE_SIZE, citationLine, useLoaded } from './cardDisplay'
import { CLAIM_TYPES, STATUSES } from './vocabulary'

// ---------------------------------------------------------------------------
// The findings list
// ---------------------------------------------------------------------------

interface Filters {
  q: string
  status: string
  claim_type: string
  theme: string
}

const NO_FILTERS: Filters = { q: '', status: '', claim_type: '', theme: '' }

/** What the list is currently asking the server for, and what came back. */
interface FindingQuery {
  filters: Filters
  setFilters: (filters: Filters) => void
  sort: FindingSort
  descending: boolean
  toggleSort: (column: FindingSort) => void
  offset: number
  setOffset: (offset: number) => void
  state: ReturnType<typeof useLoaded<FindingPage>>['state']
  reload: () => void
}

/**
 * The question the list is asking: which findings, in what order, which page.
 *
 * All three of the List UX features are server-side here, so all three are one
 * concern — the filters, the sort and the offset only mean anything as a single
 * request. Keeping them together is also what makes the two corrective effects
 * below obviously right rather than incidental.
 */
function useFindingQuery(corpus: string | null): FindingQuery {
  const [filters, setFilters] = useState<Filters>(NO_FILTERS)
  const [applied, setApplied] = useState<Filters>(NO_FILTERS)
  const [sort, setSort] = useState<FindingSort>('updated')
  const [descending, setDescending] = useState(true)
  const [offset, setOffset] = useState(0)

  // Typing should not fire a request per keystroke, and the server is the only
  // thing that can filter correctly across pages — so the text fields settle
  // first and then ask.
  useEffect(() => {
    const timer = setTimeout(() => setApplied(filters), FILTER_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [filters])

  // Any change to what is being asked for starts again at the first page.
  // Staying on page four of a list that now has two is how a reader concludes
  // their findings have vanished.
  useEffect(() => {
    setOffset(0)
  }, [applied, sort, descending])

  const key = JSON.stringify([corpus, applied, sort, descending, offset])
  const { state, reload } = useLoaded<FindingPage>(
    () =>
      listFindings({
        corpus: corpus ?? undefined,
        // The reader's own work alone. Another author's findings are held
        // under quarantine and get their own section below.
        imported_from: '',
        q: applied.q || undefined,
        status: (applied.status || undefined) as never,
        claim_type: (applied.claim_type || undefined) as never,
        theme: applied.theme || undefined,
        sort,
        direction: descending ? 'desc' : 'asc',
        limit: PAGE_SIZE,
        offset,
      }),
    key,
  )

  function toggleSort(column: FindingSort) {
    if (column === sort) return setDescending((value) => !value)
    setSort(column)
    setDescending(column === 'updated' || column === 'created')
  }

  return { filters, setFilters, sort, descending, toggleSort, offset, setOffset, state, reload }
}

/** The per-column filter inputs, one per sortable column plus themes. */
function FilterRow({
  filters,
  setFilters,
}: {
  filters: Filters
  setFilters: (filters: Filters) => void
}): JSX.Element {
  return (
    <tr className="grid__filters">
      <td>
        <input
          value={filters.q}
          onChange={(event) => setFilters({ ...filters, q: event.target.value })}
          aria-label={t('findings.filter.claim')}
          placeholder={t('findings.filter.claim')}
        />
      </td>
      <td>
        <select
          value={filters.status}
          onChange={(event) => setFilters({ ...filters, status: event.target.value })}
          aria-label={t('findings.filter.status')}
        >
          <option value="">{t('filter.any')}</option>
          {STATUSES.map((status) => (
            <option key={status} value={status}>
              {t(`findings.status.${status}`)}
            </option>
          ))}
        </select>
      </td>
      <td>
        <select
          value={filters.claim_type}
          onChange={(event) => setFilters({ ...filters, claim_type: event.target.value })}
          aria-label={t('findings.filter.kind')}
        >
          <option value="">{t('filter.any')}</option>
          {CLAIM_TYPES.map((kind) => (
            <option key={kind} value={kind}>
              {t(`claim.type.${kind}`)}
            </option>
          ))}
        </select>
      </td>
      <td>
        <input
          value={filters.theme}
          onChange={(event) => setFilters({ ...filters, theme: event.target.value })}
          aria-label={t('findings.filter.themes')}
          placeholder={t('findings.filter.themes')}
        />
      </td>
      <td />
    </tr>
  )
}

/** One finding as a row: the claim it states, how it is marked, and when it last moved. */
function FindingRow({
  finding,
  onOpen,
  onRemove,
}: {
  finding: FindingSummary
  onOpen: (card: Card) => void
  onRemove: (finding: FindingSummary) => void
}): JSX.Element {
  return (
    <tr>
      <td>
        <button
          type="button"
          className="link"
          onClick={() => onOpen({ kind: 'finding', id: finding.id })}
        >
          {finding.title}
        </button>
        {citationLine(finding) && (
          <span className="grid__note muted">{citationLine(finding)}</span>
        )}
      </td>
      <td>{t(`findings.status.${finding.status}`)}</td>
      <td>{finding.claim_type ? t(`claim.type.${finding.claim_type}`) : '—'}</td>
      <td className="muted">{finding.themes.join(', ') || '—'}</td>
      <td className="muted">
        {finding.updated_at?.slice(0, 10)}
        <button type="button" className="link" onClick={() => onRemove(finding)}>
          {t('findings.delete')}
        </button>
      </td>
    </tr>
  )
}

/** The column headers that can be sorted by, plus the one that cannot. */
function HeaderRow({
  sort,
  descending,
  onSort,
}: {
  sort: FindingSort
  descending: boolean
  onSort: (column: FindingSort) => void
}): JSX.Element {
  return (
    <tr>
      <SortableHeader label={t('findings.column.claim')} column="title" sort={sort} descending={descending} onSort={onSort} />
      <SortableHeader label={t('findings.column.status')} column="status" sort={sort} descending={descending} onSort={onSort} />
      <SortableHeader label={t('findings.column.kind')} column="claim_type" sort={sort} descending={descending} onSort={onSort} />
      <th scope="col">{t('findings.column.themes')}</th>
      <SortableHeader label={t('findings.column.updated')} column="updated" sort={sort} descending={descending} onSort={onSort} />
    </tr>
  )
}

/** Previous / position / next. Renders nothing when everything fits on one page. */
function Pager({
  offset,
  total,
  onOffset,
}: {
  offset: number
  total: number
  onOffset: (offset: number) => void
}): JSX.Element | null {
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE))
  const current = Math.floor(offset / PAGE_SIZE) + 1
  if (pages <= 1) return null

  return (
    <footer className="pager">
      <button type="button" disabled={offset === 0} onClick={() => onOffset(offset - PAGE_SIZE)}>
        {t('pager.previous')}
      </button>
      <span className="muted">{t('pager.position', { current, pages })}</span>
      <button type="button" disabled={current >= pages} onClick={() => onOffset(offset + PAGE_SIZE)}>
        {t('pager.next')}
      </button>
    </footer>
  )
}

/**
 * Delete a finding, and offer to retire it when other work stands on it.
 *
 * Outside the component because it needs nothing from the render beyond a way to ask for
 * the list again — and because the interesting half is the refusal, which is a rule of the
 * study model rather than anything about this card.
 */
async function removeFinding(finding: FindingSummary, reload: () => void): Promise<void> {
  try {
    await deleteFinding(finding.id)
    reload()
  } catch (error) {
    // A refused delete is not a failure of the request — other work stands
    // on this claim. Retiring is the framework's own answer, so offer it.
    const blocked =
      // standards: const-duplicated-literal exempt -- the server's own name for this value on the wire. A .ts file cannot reference a
      // Python constant, so the single home stays the server and this mirrors it. The
      // paydown is generating the vocabulary into TypeScript rather than carrying a marker
      // per site -- see docs/engineering-standards.md.
      error instanceof Error && 'kind' in error && error.kind === 'blocked_by_dependents'
    if (!blocked) throw error
    if (window.confirm(`${(error as Error).message}\n\n${t('findings.retireInstead')}`)) {
      await retireFinding(finding.id)
      reload()
    }
  }
}

export function FindingsCard({
  corpus,
  onOpen,
}: {
  corpus: string | null
  onOpen: (card: Card) => void
}): JSX.Element {
  const { filters, setFilters, sort, descending, toggleSort, offset, setOffset, state, reload } =
    useFindingQuery(corpus)

  const header = (
    <header className="card__head">
      <h2 className="card__kind">{t('findings.title')}</h2>
      <button type="button" className="link" onClick={() => onOpen({ kind: 'findingEditor', id: null })}>
        {t('findings.new')}
      </button>
    </header>
  )

  if (state.status === 'loading') return <article>{header}<Loading /></article>
  if (state.status === 'error')
    return <article>{header}<Failure message={state.message} /></article>

  const page = state.data

  return (
    <article>
      {header}

      <table className="grid">
        <thead>
          <HeaderRow sort={sort} descending={descending} onSort={toggleSort} />
          <FilterRow filters={filters} setFilters={setFilters} />
        </thead>
        <tbody>
          {page.findings.map((finding) => (
            <FindingRow
              key={finding.id}
              finding={finding}
              onOpen={onOpen}
              onRemove={(target) => void removeFinding(target, reload)}
            />
          ))}
        </tbody>
      </table>

      {page.total === 0 && <p className="muted">{t('findings.empty')}</p>}

      <Pager offset={offset} total={page.total} onOffset={setOffset} />

      <ImportedSections corpus={corpus} onOpen={onOpen} onChanged={() => setOffset(0)} />
      <Subscriptions onChanged={() => setOffset(0)} />
      {/* Below the quarantine rather than above it: a reader arrives at this
          page to read what they have, and the way to get more belongs after
          that rather than in front of it. */}
      <BringFindingsIn corpus={corpus} onChanged={() => setOffset(0)} onOpen={onOpen} />
    </article>
  )
}

function SortableHeader({
  label,
  column,
  sort,
  descending,
  onSort,
}: {
  label: string
  column: FindingSort
  sort: FindingSort
  descending: boolean
  onSort: (column: FindingSort) => void
}) {
  const active = sort === column
  return (
    <th scope="col" aria-sort={active ? (descending ? 'descending' : 'ascending') : 'none'}>
      <button type="button" className="link" onClick={() => onSort(column)}>
        {label}
        <span aria-hidden="true">{active ? (descending ? ' ↓' : ' ↑') : ''}</span>
      </button>
    </th>
  )
}

// The imported-corpora views moved to `imports.tsx` — a different subject
// (whose work it is) that had grown into a section of its own inside this
// file. `ImportedCard` is re-exported because `workspace/index.tsx` is the one
// public door onto this directory and callers name it there, not here.
export { ImportedCard } from './imports'
